import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { normalizeHomeSettings } from "../utils/homeContent";
import { formatKolkataDate } from "../utils/dateTime";
import { sweetAlert, sweetConfirm } from "../utils/sweetAlerts";
import { RUNTIME_PUBLIC_BASE, RUNTIME_API_BASE } from "../utils/runtimeBase";

const API = RUNTIME_PUBLIC_BASE;
const API_V1 = RUNTIME_API_BASE;

function uid(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const runCommand = (command) => {
    document.execCommand(command, false, null);
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div style={{ border: "1px solid #dbe3f0", borderRadius: "14px", overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", gap: "8px", padding: "10px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runCommand("bold")}>
          Bold
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runCommand("italic")}>
          Italic
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        style={{
          minHeight: "110px",
          padding: "12px 14px",
          outline: "none",
          fontFamily: "'Anek Bangla', 'Segoe UI', sans-serif",
          lineHeight: 1.7,
        }}
      />
    </div>
  );
}

function HomeSetting() {
  const [settings, setSettings] = useState(() => normalizeHomeSettings({}));
  const [banners, setBanners] = useState([]);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerTitle, setBannerTitle] = useState("");
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const headerImageUrl = useMemo(() => {
    if (headerImageFile) {
      return URL.createObjectURL(headerImageFile);
    }
    return settings.banner_image ? `${API}/uploads/${settings.banner_image}` : "";
  }, [headerImageFile, settings.banner_image]);

  useEffect(() => {
    return () => {
      if (headerImageFile) {
        URL.revokeObjectURL(headerImageUrl);
      }
    };
  }, [headerImageFile, headerImageUrl]);

  const loadPageData = async () => {
    try {
      const [settingsRes, bannersRes] = await Promise.all([
        axios.get(`${API_V1}/home-settings`),
        axios.get(`${API_V1}/home-banners`),
      ]);

      setSettings(normalizeHomeSettings(settingsRes.data));
      setBanners(Array.isArray(bannersRes.data) ? bannersRes.data : []);
    } catch {
      setError("Failed to load home page settings");
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  useEffect(() => {
    if (!preview) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [preview]);

  useEffect(() => {
    if (!preview) return;
    const handleEsc = (event) => {
      if (event.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [preview]);

  const setField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateListItem = (key, index, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: prev[key].map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addListItem = (key, item) => {
    setSettings((prev) => ({
      ...prev,
      [key]: [...prev[key], item],
    }));
  };

  const removeListItem = (key, index) => {
    setSettings((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const uploadBanner = async () => {
    if (!bannerFile) {
      setError("Please select a hero banner image first");
      return;
    }

    try {
      setUploadingBanner(true);
      setError("");
      setMessage("");

      const formData = new FormData();
      formData.append("image", bannerFile);
      formData.append("title", bannerTitle);

      await axios.post(`${API_V1}/home-banners`, formData);
      setBannerFile(null);
      setBannerTitle("");
      setMessage("Hero banner uploaded successfully");
      loadPageData();
    } catch {
      setError("Hero banner upload failed");
    } finally {
      setUploadingBanner(false);
    }
  };

  const deleteBanner = async (id) => {
    if (!(await sweetConfirm("Delete this hero banner?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      await axios.delete(`${API_V1}/home-banners/${id}`);
      setMessage("Hero banner deleted successfully");
      loadPageData();
    } catch {
      setError("Banner delete failed");
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      const formData = new FormData();
      [
        "subtitle",
        "title",
        "description",
        "hero_primary_button_text",
        "hero_primary_button_link",
        "hero_secondary_button_text",
        "hero_secondary_button_link",
        "hero_content_visible",
        "categories_section_title",
        "categories_section_subtitle",
        "counters_section_title",
        "features_section_title",
        "features_section_subtitle",
        "affiliations_section_title",
      ].forEach((key) => {
        if (key === "hero_content_visible") {
          formData.append(key, settings.hero_content_visible ? "1" : "0");
          return;
        }
        formData.append(key, settings[key] || "");
      });

      formData.append("header_image_height_desktop", String(settings.header_image_height_desktop || 140));
      formData.append("header_image_height_mobile", String(settings.header_image_height_mobile || 100));
      formData.append("notices", JSON.stringify(settings.notices || []));
      formData.append("categories", JSON.stringify(settings.categories || []));
      formData.append("counters", JSON.stringify(settings.counters || []));
      formData.append("features", JSON.stringify(settings.features || []));
      formData.append(
        "affiliations",
        JSON.stringify(
          (settings.affiliations || []).map((item) => ({
            id: item.id,
            title: item.title || "",
            image: item.image || "",
          }))
        )
      );

      if (headerImageFile) {
        formData.append("banner_image_file", headerImageFile);
      }

      (settings.affiliations || []).forEach((item) => {
        if (item.file) {
          formData.append("affiliation_file_keys[]", item.id);
          formData.append("affiliation_files[]", item.file);
        }
      });

      await axios.post(`${API_V1}/home-settings`, formData);
      setHeaderImageFile(null);
      await sweetAlert("Home page settings saved successfully", {
        icon: "success",
        timer: 2200,
      });
      loadPageData();
    } catch {
      const msg = "Home page settings save failed";
      setError(msg);
      await sweetAlert(msg, { icon: "error", timer: 3200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ display: "grid", gap: "22px" }}>
        <div>
          <h3 style={{ marginBottom: "6px" }}>Home Page Setting</h3>
          <p className="text-muted mb-0">
            Header image, hero banners, multiple notices, quick access cards, counters, features, and affiliation logos can now be managed from here.
          </p>
        </div>
        <div className="card border-0 shadow-sm">
          <div className="card-body d-flex justify-content-end">
            <button type="button" className="btn btn-primary px-4" onClick={saveSettings} disabled={saving}>
              {saving ? "Saving..." : "Save All Home Settings"}
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h5>Header Image Panel</h5>
            <p className="text-muted mb-3">
              Current display size label: Desktop {settings.header_image_height_desktop}px, Mobile {settings.header_image_height_mobile}px
            </p>
            <div className="row g-3 align-items-end">
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Header Image Upload</label>
                <input type="file" className="form-control" onChange={(event) => setHeaderImageFile(event.target.files?.[0] || null)} />
              </div>
              <div className="col-lg-2">
                <label className="form-label fw-semibold">Desktop Height</label>
                <input
                  type="number"
                  className="form-control"
                  value={settings.header_image_height_desktop}
                  onChange={(event) => setField("header_image_height_desktop", event.target.value)}
                />
              </div>
              <div className="col-lg-2">
                <label className="form-label fw-semibold">Mobile Height</label>
                <input
                  type="number"
                  className="form-control"
                  value={settings.header_image_height_mobile}
                  onChange={(event) => setField("header_image_height_mobile", event.target.value)}
                />
              </div>
              <div className="col-lg-4">
                {headerImageUrl ? (
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", padding: "8px", background: "#fff" }}>
                    <img src={headerImageUrl} alt="Header preview" style={{ width: "100%", maxHeight: "120px", objectFit: "contain" }} />
                  </div>
                ) : (
                  <div className="text-muted">No header image uploaded yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h5>Hero Section Content</h5>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Hero Eyebrow</label>
                <input className="form-control" value={settings.subtitle} onChange={(event) => setField("subtitle", event.target.value)} />
              </div>
              <div className="col-md-8">
                <label className="form-label fw-semibold">Hero Title</label>
                <input className="form-control" value={settings.title} onChange={(event) => setField("title", event.target.value)} />
              </div>
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hero-content-visible-toggle"
                    checked={!!settings.hero_content_visible}
                    onChange={(event) => setField("hero_content_visible", event.target.checked)}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="hero-content-visible-toggle">
                    Show Hero Section Content (Title, Description, Buttons)
                  </label>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Hero Description</label>
                <textarea className="form-control" rows="4" value={settings.description} onChange={(event) => setField("description", event.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Primary Button Text</label>
                <input className="form-control" value={settings.hero_primary_button_text} onChange={(event) => setField("hero_primary_button_text", event.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Primary Button Link</label>
                <input className="form-control" value={settings.hero_primary_button_link} onChange={(event) => setField("hero_primary_button_link", event.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Secondary Button Text</label>
                <input className="form-control" value={settings.hero_secondary_button_text} onChange={(event) => setField("hero_secondary_button_text", event.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Secondary Button Link</label>
                <input className="form-control" value={settings.hero_secondary_button_link} onChange={(event) => setField("hero_secondary_button_link", event.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Scrolling Notice Manager</h5>
                <div className="text-muted">Add multiple Bangla or English notices. Bold and italic text are supported.</div>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => addListItem("notices", { content: "" })}
              >
                Add Notice
              </button>
            </div>

            <div className="d-grid gap-3">
              {settings.notices.map((notice, index) => (
                <div key={`notice-${index}`} className="border rounded-4 p-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Notice {index + 1}</strong>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeListItem("notices", index)}>
                      Remove
                    </button>
                  </div>
                  <RichTextEditor value={notice.content || ""} onChange={(value) => updateListItem("notices", index, "content", value)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h5>Quick Access Category Cards</h5>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Section Title</label>
                <input className="form-control" value={settings.categories_section_title} onChange={(event) => setField("categories_section_title", event.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Section Subtitle</label>
                <input className="form-control" value={settings.categories_section_subtitle} onChange={(event) => setField("categories_section_subtitle", event.target.value)} />
              </div>
            </div>

            <div className="d-grid gap-3">
              {settings.categories.map((item, index) => (
                <div key={`category-${index}`} className="row g-2 border rounded-4 p-2 bg-light">
                  <div className="col-md-3">
                    <input className="form-control" placeholder="Title" value={item.title} onChange={(event) => updateListItem("categories", index, "title", event.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <input className="form-control" placeholder="Helper" value={item.helper} onChange={(event) => updateListItem("categories", index, "helper", event.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <input className="form-control" placeholder="Icon class" value={item.icon} onChange={(event) => updateListItem("categories", index, "icon", event.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <input className="form-control" placeholder="Path" value={item.path} onChange={(event) => updateListItem("categories", index, "path", event.target.value)} />
                  </div>
                  <div className="col-md-1 d-flex">
                    <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeListItem("categories", index)}>
                      X
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => addListItem("categories", { title: "", helper: "", icon: "bi-star-fill", path: "/" })}
              >
                Add Category Card
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5>Counter Section</h5>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input className="form-control" value={settings.counters_section_title} onChange={(event) => setField("counters_section_title", event.target.value)} />
                </div>
                <div className="d-grid gap-3">
                  {settings.counters.map((item, index) => (
                    <div key={`counter-${index}`} className="row g-2 border rounded-4 p-2 bg-light">
                      <div className="col-7">
                        <input className="form-control" placeholder="Label" value={item.label} onChange={(event) => updateListItem("counters", index, "label", event.target.value)} />
                      </div>
                      <div className="col-4">
                        <input className="form-control" type="number" placeholder="Value" value={item.value} onChange={(event) => updateListItem("counters", index, "value", event.target.value)} />
                      </div>
                      <div className="col-1 d-flex">
                        <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeListItem("counters", index)}>
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline-primary" onClick={() => addListItem("counters", { label: "", value: 0 })}>
                    Add Counter
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5>Feature Section</h5>
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Section Title</label>
                    <input className="form-control" value={settings.features_section_title} onChange={(event) => setField("features_section_title", event.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Section Subtitle</label>
                    <textarea className="form-control" rows="3" value={settings.features_section_subtitle} onChange={(event) => setField("features_section_subtitle", event.target.value)} />
                  </div>
                </div>
                <div className="d-grid gap-3">
                  {settings.features.map((item, index) => (
                    <div key={`feature-${index}`} className="row g-2 border rounded-4 p-2 bg-light">
                      <div className="col-md-3">
                        <input className="form-control" placeholder="Title" value={item.title} onChange={(event) => updateListItem("features", index, "title", event.target.value)} />
                      </div>
                      <div className="col-md-5">
                        <input className="form-control" placeholder="Description" value={item.desc} onChange={(event) => updateListItem("features", index, "desc", event.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <input className="form-control" placeholder="Icon class" value={item.icon} onChange={(event) => updateListItem("features", index, "icon", event.target.value)} />
                      </div>
                      <div className="col-md-1 d-flex">
                        <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeListItem("features", index)}>
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline-primary" onClick={() => addListItem("features", { title: "", desc: "", icon: "bi-star-fill" })}>
                    Add Feature
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Affiliation Logo Slider</h5>
                <div className="text-muted">Upload multiple logos that will move in the home page affiliation slider.</div>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => addListItem("affiliations", { id: uid("aff"), title: "", image: "", file: null })}
              >
                Add Affiliation Logo
              </button>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Section Title</label>
              <input className="form-control" value={settings.affiliations_section_title} onChange={(event) => setField("affiliations_section_title", event.target.value)} />
            </div>

            <div className="d-grid gap-3">
              {settings.affiliations.map((item, index) => (
                <div key={item.id || `aff-${index}`} className="row g-3 border rounded-4 p-3 bg-light">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Logo Title</label>
                    <input className="form-control" value={item.title || ""} onChange={(event) => updateListItem("affiliations", index, "title", event.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Replace Logo</label>
                    <input
                      type="file"
                      className="form-control"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        setSettings((prev) => ({
                          ...prev,
                          affiliations: prev.affiliations.map((affiliation, itemIndex) =>
                            itemIndex === index ? { ...affiliation, file } : affiliation
                          ),
                        }));
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    {(item.file || item.image) ? (
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "8px", background: "#fff" }}>
                        <img
                          src={item.file ? URL.createObjectURL(item.file) : `${API}/uploads/${item.image}`}
                          alt={item.title || "Affiliation logo"}
                          style={{ width: "100%", maxHeight: "74px", objectFit: "contain" }}
                        />
                      </div>
                    ) : (
                      <div className="text-muted">No logo selected.</div>
                    )}
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeListItem("affiliations", index)}>
                      X
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Hero Banner Manager</h5>
                <div className="text-muted">Recommended visual size 1920X700</div>
              </div>
            </div>

            <div className="row g-3 align-items-end mb-4">
              <div className="col-md-5">
                <label className="form-label fw-semibold">Banner Image</label>
                <input type="file" className="form-control" onChange={(event) => setBannerFile(event.target.files?.[0] || null)} />
              </div>
              <div className="col-md-5">
                <label className="form-label fw-semibold">Banner Title</label>
                <input className="form-control" value={bannerTitle} onChange={(event) => setBannerTitle(event.target.value)} />
              </div>
              <div className="col-md-2">
                <button type="button" className="btn btn-primary w-100" onClick={uploadBanner} disabled={uploadingBanner}>
                  {uploadingBanner ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>

            <div className="row g-4">
              {banners.map((banner) => (
                <div key={banner.id} className="col-lg-3 col-md-4 col-sm-6">
                  <div className="border rounded-4 overflow-hidden bg-white h-100 shadow-sm">
                    <img
                      src={`${API}/uploads/${banner.image}`}
                      alt={banner.title || "Hero banner"}
                      style={{ width: "100%", height: "170px", objectFit: "cover", cursor: "pointer" }}
                      onClick={() => setPreview(`${API}/uploads/${banner.image}`)}
                    />
                    <div className="p-3">
                      <div className="fw-semibold">{banner.title || "No title"}</div>
                      <div className="text-muted small">{formatKolkataDate(banner.created_at)}</div>
                    </div>
                    <div className="p-3 pt-0">
                      <button type="button" className="btn btn-outline-danger w-100" onClick={() => deleteBanner(banner.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-primary px-4" onClick={saveSettings} disabled={saving}>
            {saving ? "Saving..." : "Save All Home Settings"}
          </button>
        </div>
      </div>

      {preview && typeof document !== "undefined"
        ? createPortal(
            <div
              onClick={() => setPreview(null)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 30000,
                background: "rgba(0, 0, 0, 0.78)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "18px",
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: "min(1200px, 96vw)",
                  maxHeight: "92vh",
                  background: "#ffffff",
                  borderRadius: "14px",
                  overflow: "hidden",
                  boxShadow: "0 18px 55px rgba(0,0,0,0.35)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ padding: "10px 12px", borderBottom: "1px solid #e5e7eb", textAlign: "end" }}>
                  <button className="btn btn-danger btn-sm" onClick={() => setPreview(null)}>
                    Close
                  </button>
                </div>
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "220px",
                  }}
                >
                  <img src={preview} alt="Preview" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", display: "block" }} />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </DashboardLayout>
  );
}

export default HomeSetting;



